/**
 * Responsibility: show whether the news feature can actually say anything.
 *
 * This panel exists because of a real near-miss. The first source list shipped
 * with seven of ten feeds returning 404 or 403, leaving a maximum achievable
 * score below the corroboration threshold: the system would have polled
 * politely forever and never once raised a signal, and nothing on screen would
 * have looked wrong. A feed that quietly dies looks exactly like a quiet news
 * week, so the difference is made visible here rather than left in the logs.
 */
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { SectionCard } from '@kit/components';
import type { NewsRegistryHealth } from '../../api/client';

const TIER_LABEL: Record<string, string> = {
  OFFICIAL: 'Official',
  ESTABLISHED: 'Outlet',
  COMMUNITY: 'Community',
};

export function SourceHealthPanel({ health }: { health: NewsRegistryHealth }) {
  return (
    <SectionCard
      title="Sources"
      subtitle={`A signal needs ${health.min_distinct_sources} independent sources and a score of ${health.min_score}. These sources can reach ${health.max_possible_score}.`}
    >
      <Stack spacing={2}>
        {!health.can_corroborate ? (
          <Alert severity="error">
            The enabled sources cannot reach the corroboration threshold. Nothing will ever be
            reported as more than a rumour until more feeds are working.
          </Alert>
        ) : null}

        {!health.has_official_source ? (
          <Alert severity="info">
            No official Pokemon feed is reachable, so nothing here will ever read as Confirmed.
            That is a known gap rather than evidence that nothing gets announced.
          </Alert>
        ) : null}

        <Stack spacing={1}>
          {health.sources.map((source) => (
            <Box
              key={source.key}
              sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}
            >
              <Chip
                size="small"
                variant="outlined"
                color={source.enabled ? 'success' : 'default'}
                label={source.enabled ? `weight ${source.weight}` : 'off'}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, opacity: source.enabled ? 1 : 0.55 }}
              >
                {source.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {TIER_LABEL[source.tier] ?? source.tier}
              </Typography>
              {source.note ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexBasis: '100%', opacity: 0.8 }}
                >
                  {source.note}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Stack>
      </Stack>
    </SectionCard>
  );
}
